/**
 * API and Schema Versioning Configuration
 *
 * This module manages API and schema versions for the compliance-ui application.
 * It provides version headers for all API requests and handles version responses.
 *
 * Version Management Strategy (Priority Order):
 * API Version:
 *   1. Environment Variable: VITE_API_VERSION (.env file)
 *   2. Package.json: "apiVersion" field
 *
 * Schema Version:
 *   1. Developer Override: Set VITE_SCHEMA_VERSION in .env file
 *   2. Package.json: Use "schemaVersion" field if specified
 *   3. Backend Dynamic: Fetch from /api/versions endpoint
 *   4. Fallback: Use package.json "schemaVersion" field
 *
 * Developer Configuration Options:
 * - Environment Variables (.env file):
 *   VITE_API_VERSION=1.0
 *   VITE_SCHEMA_VERSION=1.0.236
 * - Package.json:
 *   "apiVersion": "1.0"
 *   "schemaVersion": "1.0.236"
 * - Runtime: Use setSchemaVersion() function or VersionSettings UI
 *
 * Examples:
 * // .env file
 * VITE_API_VERSION=1.0
 * VITE_SCHEMA_VERSION=1.0.236
 *
 * // package.json
 * {
 *   "version": "0.1.216",
 *   "apiVersion": "1.0",
 *   "schemaVersion": "1.0.236"
 * }
 */

import packageJson from '../../package.json';
import { reportError, debug } from '../services/logger';

// API version - can be overridden via VITE_API_VERSION env variable
export const API_VERSION = import.meta.env.VITE_API_VERSION || packageJson.apiVersion;

// Developer-configured versions (highest priority)
const DEVELOPER_API_VERSION = import.meta.env.VITE_API_VERSION || null;
const DEVELOPER_SCHEMA_VERSION = import.meta.env.VITE_SCHEMA_VERSION || packageJson.schemaVersion;

// Storage keys for version information
const VERSION_STORAGE_KEY = 'api_version_info';
const SCHEMA_VERSION_KEY = 'current_schema_version';

// Runtime schema version (fetched from backend)
let runtimeSchemaVersion = null;

/**
 * Fetch version information from the backend
 * @param {boolean} forceBackend - If true, ignore developer override and use backend version
 * @returns {Promise<Object>} Version information from the server
 */
export const fetchVersionInfo = async (forceBackend = false) => {
  try {
    const baseUrl = (import.meta.env.VITE_BASE_URL || 'https://localhost:8080').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/api/versions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch version info: ${response.status}`);
    }

    const versionInfo = await response.json();

    // Store the version info
    storeVersionInfo(versionInfo);

    // Set runtime schema version based on priority
    if (DEVELOPER_SCHEMA_VERSION && !forceBackend) {
      // Developer has explicitly configured a version - use it
      runtimeSchemaVersion = DEVELOPER_SCHEMA_VERSION;
      debug('versioning.schema', `developer-configured: ${DEVELOPER_SCHEMA_VERSION}`);
    } else {
      // Use backend's recommended version
      runtimeSchemaVersion = versionInfo.schema_version;
      debug('versioning.schema', `backend: ${versionInfo.schema_version}`);
    }

    sessionStorage.setItem(SCHEMA_VERSION_KEY, runtimeSchemaVersion);

    return versionInfo;
  } catch (error) {
    reportError('versioning.fetch', error);
    // Fallback priority: developer config > package.json schemaVersion
    runtimeSchemaVersion = DEVELOPER_SCHEMA_VERSION || packageJson.schemaVersion;
    return null;
  }
};

/**
 * Get current schema version (runtime or fallback)
 * Priority: runtime override > session storage > developer config > package.json
 * @returns {string} Current schema version to use
 */
export const getSchemaVersion = () => {
  if (runtimeSchemaVersion) {
    return runtimeSchemaVersion;
  }

  // Try to get from session storage
  const stored = sessionStorage.getItem(SCHEMA_VERSION_KEY);
  if (stored) {
    runtimeSchemaVersion = stored;
    return stored;
  }

  // Use developer-configured version if available
  if (DEVELOPER_SCHEMA_VERSION) {
    return DEVELOPER_SCHEMA_VERSION;
  }

  // Fallback to package.json schemaVersion
  return packageJson.schemaVersion;
};

/**
 * Get the developer-configured schema version (from env or package.json)
 * @returns {string|null} Developer-configured version or null
 */
export const getDeveloperSchemaVersion = () => {
  return DEVELOPER_SCHEMA_VERSION || null;
};

/**
 * Get the developer-configured API version (from env)
 * @returns {string|null} Developer-configured API version or null
 */
export const getDeveloperApiVersion = () => {
  return DEVELOPER_API_VERSION || null;
};

/**
 * Set schema version manually (for testing or admin override)
 * @param {string} version - Schema version to use
 */
export const setSchemaVersion = version => {
  runtimeSchemaVersion = version;
  sessionStorage.setItem(SCHEMA_VERSION_KEY, version);
};

/**
 * Get version headers to include in API requests
 * @returns {Object} Headers object with version information
 */
export const getVersionHeaders = () => ({
  'X-API-Version': API_VERSION,
  'X-Schema-Version': getSchemaVersion()
});

/**
 * Store version information received from the server
 * @param {Object} versionInfo - Version information from /api/versions endpoint
 */
export const storeVersionInfo = versionInfo => {
  if (!versionInfo) return;

  const storageData = {
    apiVersion: versionInfo.api_version,
    schemaVersion: versionInfo.schema_version,
    supportedSchemaVersions: versionInfo.supported_schema_versions || [],
    minRequiredSchemaVersion: versionInfo.min_required_schema_version,
    timestamp: new Date().toISOString()
  };

  // Store in sessionStorage for debugging and monitoring
  try {
    sessionStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(storageData));
  } catch (e) {
    reportError('versioning.store', e);
  }
};

/**
 * Get stored version information from the server
 * @returns {Object|null} Version information object or null if not available
 */
export const getStoredVersionInfo = () => {
  try {
    const stored = sessionStorage.getItem(VERSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    reportError('versioning.read', e);
    return null;
  }
};

/**
 * Check if the client version is compatible with server versions
 * @returns {Object} Compatibility status
 */
export const checkVersionCompatibility = () => {
  const versionInfo = getStoredVersionInfo();

  if (!versionInfo) {
    return {
      isCompatible: true,
      message: 'Version information not yet available'
    };
  }

  const currentSchemaVersion = getSchemaVersion();
  const isApiCompatible = versionInfo.apiVersion === API_VERSION;
  const isSchemaCompatible = versionInfo.supportedSchemaVersions.includes(currentSchemaVersion);

  return {
    isCompatible: isApiCompatible && isSchemaCompatible,
    isApiCompatible,
    isSchemaCompatible,
    clientApiVersion: API_VERSION,
    clientSchemaVersion: currentSchemaVersion,
    serverApiVersion: versionInfo.apiVersion,
    serverSchemaVersion: versionInfo.schemaVersion,
    supportedSchemaVersions: versionInfo.supportedSchemaVersions,
    message: !isApiCompatible
      ? `API version ${API_VERSION} not supported. Server is using: ${versionInfo.apiVersion}`
      : !isSchemaCompatible
        ? `Schema version ${currentSchemaVersion} not supported. Server supports: ${versionInfo.supportedSchemaVersions.join(', ')}`
        : 'Versions are compatible'
  };
};

/**
 * Handle version mismatch scenarios
 * @param {Object} compatibility - Compatibility check result
 */
export const handleVersionMismatch = compatibility => {
  if (!compatibility.isCompatible) {
    debug('versioning.mismatch', compatibility.message);

    // You can implement custom logic here:
    // - Show a notification to the user
    // - Redirect to an update page
    // - Use a fallback API version
    // - Log to monitoring service

    // For now, we'll just log it
    if (!compatibility.isApiCompatible) {
      reportError('versioning.apiMismatch', new Error('API version mismatch'), {
        client: compatibility.clientApiVersion,
        server: compatibility.serverApiVersion
      });
    }

    if (!compatibility.isSchemaCompatible) {
      reportError('versioning.schemaMismatch', new Error('Schema version mismatch'), {
        client: compatibility.clientSchemaVersion,
        server: compatibility.serverSchemaVersion
      });
    }
  }
};
